import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { TrendingUp, TrendingDown, Package, Truck, Users, AlertCircle, DollarSign, ArrowRight, ShoppingCart, BarChart3 } from 'lucide-react';
import { PageLayout } from '../common/index.js';

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => {
    fetchAnalytics();
  }, [filter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' 
        ? { filter, ...customDates } 
        : { filter };

      const [dashboardRes, profitLossRes] = await Promise.all([
        analyticsService.getDashboardSummary(),
        analyticsService.getProfitLoss(params)
      ]);

      setDashboardData(dashboardRes);
      setProfitLossData(profitLossRes);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color, onClick }) => (
    <div 
      className={`bg-white rounded-card shadow-card p-4 lg:p-6 hover:shadow-medium transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-1">{value}</h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center">
          {trend.type === 'up' ? (
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
          )}
          <span className={`text-xs lg:text-sm ${trend.type === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend.value}
          </span>
        </div>
      )}
      {onClick && (
        <div className="mt-3 flex items-center text-brand text-sm">
          <span>View Details</span>
          <ArrowRight className="w-4 h-4 ml-1" />
        </div>
      )}
    </div>
  );

  const ModuleCard = ({ title, description, icon: Icon, color, onClick }) => (
    <div 
      className="bg-white rounded-card shadow-card p-4 lg:p-6 hover:shadow-medium transition-all cursor-pointer transform hover:-translate-y-1"
      onClick={onClick}
    >
      <div className="flex items-center mb-4">
        <div className={`p-3 rounded-full ${color} mr-4`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      <div className="flex items-center text-brand text-sm font-medium">
        <span>View Analytics</span>
        <ArrowRight className="w-4 h-4 ml-1" />
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout title="Analytics Dashboard" icon={BarChart3}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Analytics Dashboard"
      icon={BarChart3}
      actions={
        <DateFilterDropdown
          filter={filter}
          setFilter={setFilter}
          customDates={customDates}
          setCustomDates={setCustomDates}
          onApply={fetchAnalytics}
          dateRange={profitLossData?.filter}
        />
      }
    >

                {/* Profit/Loss Overview */}
                {profitLossData && (
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard
                      title="Total Revenue"
                      value={formatCurrency(profitLossData.summary.total_revenue)}
                      subtitle="Total income generated"
                      icon={DollarSign}
                      color="bg-green-500"
                    />
                    <StatCard
                      title="Total Expenses"
                      value={formatCurrency(profitLossData.summary.total_expenses)}
                      subtitle="Total expenditure"
                      icon={DollarSign}
                      color="bg-red-500"
                    />
                    <StatCard
                      title="Net Profit/Loss"
                      value={formatCurrency(profitLossData.summary.net_profit_loss)}
                      subtitle={`${profitLossData.summary.status === 'profit' ? 'Profit' : 'Loss'}`}
                      icon={profitLossData.summary.status === 'profit' ? TrendingUp : TrendingDown}
                      color={profitLossData.summary.status === 'profit' ? 'bg-green-600' : 'bg-red-600'}
                    />
                    <StatCard
                      title="Profit Margin"
                      value={`${profitLossData.summary.profit_margin_percentage.toFixed(2)}%`}
                      subtitle="Revenue to profit ratio"
                      icon={TrendingUp}
                      color="bg-purple-500"
                    />
                  </div>
                )}

                {/* Quick Stats from Dashboard */}
                {dashboardData && (
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard
                      title="Total Shipments"
                      value={dashboardData.shipments.total_count}
                      subtitle={formatCurrency(dashboardData.shipments.total_revenue)}
                      icon={Package}
                      color="bg-brand"
                      onClick={() => navigate('/analytics/shipments')}
                    />
                    <StatCard
                      title="Active Users"
                      value={dashboardData.resources.active_users}
                      subtitle="Employees & Agents"
                      icon={Users}
                      color="bg-indigo-500"
                      onClick={() => navigate('/analytics/user-performance')}
                    />
                    <StatCard
                      title="Active Vehicles"
                      value={dashboardData.resources.active_vehicles}
                      subtitle="Fleet on duty"
                      icon={Truck}
                      color="bg-yellow-500"
                      onClick={() => navigate('/analytics/vehicles')}
                    />
                    <StatCard
                      title="Outstanding Collections"
                      value={formatCurrency(dashboardData.shipments.pending)}
                      subtitle="To be collected"
                      icon={AlertCircle}
                      color="bg-blue-600"
                    />
                  </div>
                )}

                {/* Inventory & Expense Overview (NEW) */}
                {dashboardData?.inventory && dashboardData?.expenses && (
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                    
                    <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                      <div className="flex items-center mb-4">
                        <div className="p-3 rounded-full bg-rose-500 mr-3">
                          <ShoppingCart className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Expense Overview</h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total Expenses:</span>
                          <span className="text-lg font-bold text-gray-800">{formatCurrency(dashboardData.expenses.total_expenses)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Paid:</span>
                          <span className="text-sm font-semibold text-green-600">{formatCurrency(dashboardData.expenses.paid_expenses)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Pending:</span>
                          <span className="text-sm font-semibold text-blue-600">{formatCurrency(dashboardData.expenses.pending_expenses)}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Top Categories:</span>
                          <div className="mt-2 space-y-1">
                            {Object.entries(dashboardData.expenses.by_category).slice(0, 3).map(([cat, amount]) => (
                              <div key={cat} className="flex justify-between text-xs">
                                <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                                <span className="font-semibold">{formatCurrency(amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Module Cards */}
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Detailed Analytics</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <ModuleCard
                      title="Shipment Analytics"
                      description="Detailed shipment data by status, user, vehicle, payment mode, and location"
                      icon={Package}
                      color="bg-brand"
                      onClick={() => navigate('/analytics/shipments')}
                    />
                    <ModuleCard
                      title="User Performance"
                      description="Agent and employee performance metrics with collection rates"
                      icon={Users}
                      color="bg-indigo-500"
                      onClick={() => navigate('/analytics/user-performance')}
                    />
                    <ModuleCard
                      title="Vehicle Analytics"
                      description="Fleet utilization, revenue generation, and performance tracking"
                      icon={Truck}
                      color="bg-yellow-500"
                      onClick={() => navigate('/analytics/vehicles')}
                    />
                    <ModuleCard
                      title="Missing & Returned"
                      description="Track missing shipments, returns, refunds, and financial impact"
                      icon={AlertCircle}
                      color="bg-red-500"
                      onClick={() => navigate('/analytics/missing-returned')}
                    />
                    <ModuleCard
                      title="Profit & Loss Report"
                      description="Comprehensive P&L with revenue breakdown, expenses, and trends"
                      icon={TrendingUp}
                      color="bg-green-500"
                      onClick={() => navigate('/analytics/profit-loss')}
                    />
                    <ModuleCard
                      title="Commission Analytics"
                      description="Company, owner and location hub commission totals with payment status breakdown"
                      icon={DollarSign}
                      color="bg-purple-500"
                      onClick={() => navigate('/analytics/commission')}
                    />
                  </div>
                </div>

                {/* Alerts Section */}
                {dashboardData?.alerts && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-card">
                    <div className="flex items-center mb-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                      <h3 className="font-semibold text-gray-800">Alerts & Notifications</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                      {dashboardData.alerts.pending_salary_count > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Pending Salaries</span>
                          <span className="font-semibold text-blue-600">
                            {dashboardData.alerts.pending_salary_count} ({formatCurrency(dashboardData.alerts.pending_salary_amount)})
                          </span>
                        </div>
                      )}
                      {dashboardData.alerts.pending_expense_count > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Pending Expenses</span>
                          <span className="font-semibold text-blue-600">
                            {dashboardData.alerts.pending_expense_count} ({formatCurrency(dashboardData.alerts.pending_expense_amount)})
                          </span>
                        </div>
                      )}
                      {dashboardData.alerts.missing_shipments > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Missing Shipments</span>
                          <span className="font-semibold text-red-600">{dashboardData.alerts.missing_shipments}</span>
                        </div>
                      )}
                      {dashboardData.alerts.returned_shipments > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Returned Shipments</span>
                          <span className="font-semibold text-brand">{dashboardData.alerts.returned_shipments}</span>
                        </div>
                      )}
                      {dashboardData.alerts.inventory_alerts?.out_of_stock > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Out of Stock Items</span>
                          <span className="font-semibold text-red-600">{dashboardData.alerts.inventory_alerts.out_of_stock}</span>
                        </div>
                      )}
                      {dashboardData.alerts.inventory_alerts?.low_stock > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Low Stock Items</span>
                          <span className="font-semibold text-blue-600">{dashboardData.alerts.inventory_alerts.low_stock}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
    </PageLayout>
  );
};

export default Analytics;